// Keep rules in sync with netlify/functions/utils/redact.js; parity probes cover both.
/**
 * Shared secret / PII redaction primitives for anything that ends up in a log.
 *
 * Extracted from `quicklist-api.js` (OPS-56) so the value-shape redaction can be
 * reused by the actual log-safe boundary — `serializeError()` in
 * `betterstack-logger.js` — and by any other caller, instead of living only in
 * the QuickList HTTP client. This module depends on nothing else in the app, so
 * the logger can import it without a dependency cycle.
 *
 * Two complementary tools:
 *   - `redactRawText(str)` redacts value-shaped secrets/PII from free text (JWTs,
 *     OAuth/bearer tokens, sensitive `key: value` pairs, emails) while leaving
 *     benign diagnostic text ("502 Bad Gateway") intact. Use this for any string
 *     that reaches a log: an error message, an error stack, a raw response body.
 *   - `redactSensitive(value)` walks a parsed JSON structure, redacting values
 *     under sensitive key names AND running every string value through
 *     `redactRawText`. Use this for structured (object) bodies.
 */

// Keys whose values may carry secrets / PII / request internals an upstream
// error response could echo back (tokens, OAuth diagnostics, customer data).
// Matched case-insensitively as a substring of the key.
const SENSITIVE_KEY_RE =
  /(token|secret|password|passwd|pwd|auth|cookie|api[-_ ]?key|apikey|credential|session|bearer|signature|jwt|email|phone|address|ssn)/i

const REDACTED = '[redacted]'
const MAX_REDACT_DEPTH = 6

// Value-shaped secrets / PII that arbitrary (non-JSON) text can echo back: an
// HTML error page, a proxy diagnostic dump, a plain-text backend trace, an error
// message, or a stack frame. There are no keys to match here, so we redact by
// the *shape* of the value. The specific token shapes run before the generic
// `key: value` sweep so a token is caught even when it isn't adjacent to a
// recognizable key.
// A token may also start right after an escape (`next%3DeyJ…`, `key%253Dsk_live_…`),
// where `\b` does not hold because the escape ends in a hex digit (OPS-1494).
const TOKEN_START = String.raw`(?:\b|(?<=%(?:25){0,4}[0-9A-Fa-f]{2}))`
// JSON Web Tokens (header.payload.signature) are found by `redactJwts()` below:
// the old `\beyJ…` regex restarted at every `-eyJ` inside one long run, which
// is quadratic (OPS-1494).
const RAW_TOKEN_RES = [
  // Google / OAuth access tokens
  new RegExp(TOKEN_START + String.raw`ya29\.[A-Za-z0-9._-]{10,}`, 'g'),
  // Stripe secret / restricted keys (OPS-1470)
  new RegExp(TOKEN_START + String.raw`[sr]k_(?:live|test)_[A-Za-z0-9]{6,}`, 'g'),
  // AWS access key ids (OPS-1470)
  new RegExp(TOKEN_START + String.raw`(?:AKIA|ASIA)[A-Z0-9]{16}\b`, 'g'),
]
// `Bearer <token>` — keep the scheme word, drop the credential.
// The separator also accepts a url-encoded space (`%20` / `+`, or `%2520` etc.
// up to MAX_ENCODING_DEPTH levels, OPS-1494) and the credential may itself carry
// `%XX` escapes (OPS-1470). The scheme word may follow an escape directly
// (`Authorization%3A%20Bearer%20…`). `@` is part of the credential so a trailing
// `@domain` is not left behind, `[`/`]` so a value an earlier pass already cut
// (`Bearer [redacted]…`) is still consumed whole, and a repeated scheme word
// (`Bearer Bearer <token>`) is skipped.
const BEARER_SEP = String.raw`(?:\s+|%(?:25){0,4}20|\+)`
const RAW_BEARER_RE = new RegExp(
  TOKEN_START + `(bearer)${BEARER_SEP}(?:bearer${BEARER_SEP})*` + String.raw`[A-Za-z0-9._~+/%@[\]-]+=*`,
  'gi'
)
// `sensitiveKey: value` / `sensitiveKey=value` / `"sensitiveKey":"value"` in text.
// The key list is kept aligned with SENSITIVE_KEY_RE so the raw-text pass redacts
// the same key:value PII the structured walker does. The only structured keys
// intentionally absent here are the ones already covered by a dedicated pass:
// `bearer` (RAW_BEARER_RE), `auth`/`authorization` (kept as `authorization`),
// `email` (redactEmails), and `address` (RAW_ADDRESS_RE below — its value is
// multi-word and needs a wider terminator). `phone` IS included here because its
// value is a single whitespace-free token.
// `token[-_ ]?hash` is listed BEFORE the bare `token` alternative on purpose: the
// bare `token` alternative is anchored `\btoken\b`, and the trailing `\b` fails
// against `token_hash` (the `_` is a word char, so there is no boundary between
// `token` and `_hash`) — so `token` alone would NOT redact a `token_hash=…` pair.
// Adding the explicit alternative (ordered first, so the alternation prefers it)
// makes the raw-text pass catch the single-use auth `token_hash` in any body /
// stack / href string, aligning it with the URL-param denylist below (OPS-803).
const RAW_KEYVALUE_RE =
  /\b(access[-_ ]?token|refresh[-_ ]?token|id[-_ ]?token|token[-_ ]?hash|token|secret|password|passwd|pwd|client[-_ ]?secret|api[-_ ]?key|apikey|authorization|cookie|set-cookie|credential|session|signature|jwt|phone|ssn)\b(["']?\s*[:=]\s*["']?)(?:basic\s+)?([^"'&\s,;}<]+)/gi
// `address` is handled separately: a street value ("123 Main St") contains
// spaces, so the whitespace-terminated value group above would leak everything
// after the first token. Here the value runs to the next structural delimiter
// (quote, ampersand, comma, semicolon, brace, angle bracket, newline) so the
// whole address — matching the structured pass — is redacted. Over-capturing
// trailing free text only redacts more; it can never leak.
const RAW_ADDRESS_RE = /\b(address)\b(["']?\s*[:=]\s*["']?)([^"'&,;}<\r\n]+)/gi
// Emails: same matches as `/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g`
// (and the `%40` form), found by `redactEmails()` below. As a single regex the
// local part was rescanned from every position of a long run with no `@`, which
// is quadratic (OPS-1494).
const EMAIL_LOCAL_CHAR_RE = /[A-Za-z0-9._%+-]/
const EMAIL_DOMAIN_RE = /[A-Za-z0-9.-]+\.[A-Za-z]{2,}/y

// Value-shaped PII with NO key to anchor on: a bare phone number or street
// address echoed inside free text — an error message, a stack frame, an
// HTML/proxy body — e.g. `Customer 555-867-5309 at 42 Galaxy Way`. The keyed
// passes above only catch `phone=…` / `address: …`; these catch the unkeyed
// value shapes the structured walker still misses. Both are written narrowly so
// benign numeric diagnostics the reviewer called out — `502 Bad Gateway`,
// status codes, prices, SKUs/ASINs, ISO dates, order ids — are left intact:
// over-redacting diagnostic text is itself a regression.

// US / NANP phone numbers: a 3-3-4 digit run joined by REAL separators (`-`,
// `.`, space), with an optional `+<country>` prefix or `(area)` form. The
// mandatory separators are what distinguish a phone number from a bare digit run
// (an order id, an epoch); the digit lookarounds stop it biting into a longer
// number. A separator-less 10-digit run is intentionally NOT matched — it is
// indistinguishable from an id, and the keep-benign-numbers constraint wins.
const RAW_PHONE_VALUE_RE =
  /(?<!\d)(?:\+\d{1,3}[-.\s]?)?(?:\(\d{3}\)|\d{3})[-.\s]\d{3}[-.\s]\d{4}(?!\d)/g

// Street addresses: a 1–6 digit house number, 1–4 Title-Case name words, then a
// recognized street-type suffix. The Title-Case requirement AND a closed,
// case-SENSITIVE suffix list are deliberate: lowercase prose ("took 2 turns on
// the way") and benign numeric phrases ("502 Bad Gateway", "Building 3
// Conference Room") do not match, while `42 Galaxy Way` / `123 Main St` do.
const STREET_SUFFIX =
  'St|Street|Ave|Avenue|Blvd|Boulevard|Rd|Road|Dr|Drive|Ln|Lane|Way|Ct|Court|Pl|Place|Ter|Terrace|Cir|Circle|Hwy|Highway|Pkwy|Parkway|Sq|Square|Trl|Trail'
const RAW_ADDRESS_VALUE_RE = new RegExp(
  String.raw`\b\d{1,6}\s+(?:[A-Z][A-Za-z]*\.?\s+){1,4}(?:` + STREET_SUFFIX + String.raw`)\b\.?`,
  'g'
)

// Payment card numbers (OPS-1470). Separated groups (4-4-4-1..7 or Amex 4-6-5)
// joined by a consistent space/dash are matched by shape; an unseparated 13–19
// digit run is only redacted when it passes the Luhn check, so order ids and
// epoch timestamps survive.
const RAW_CARD_GROUPED_RE = /(?<!\d)(?:\d{4}([ -])\d{4}\1\d{4}\1\d{1,7}|\d{4}([ -])\d{6}\2\d{5})(?!\d)/g
const RAW_CARD_BARE_RE = /(?<![\d.])\d{13,19}(?![\d.])/g
// Encoded `key=value` pairs (OPS-1480/OPS-1494). A linear scanner rather than a
// regex: the old pattern restarted a lazy key scan at every word boundary, which
// is quadratic on long runs. An escape is `%XX`, or the same byte re-encoded up
// to MAX_ENCODING_DEPTH more times (`%25XX`, `%2525XX`, ...); every depth is read
// as the byte XX, so single/double/triple-encoded separators behave identically.
// Matching never decodes the text: benign input stays byte-identical, and only a
// sensitive value is replaced (the key and separator stay readable).
const MAX_ENCODING_DEPTH = 4
// Encoded delimiters that end an encoded key AND any value: space " # & ' , / ; < > ? { }
const ENCODED_HARD_STOPS = new Set([0x20, 0x22, 0x23, 0x26, 0x27, 0x2c, 0x2f, 0x3b, 0x3c, 0x3e, 0x3f, 0x7b, 0x7d])
// Encoded separators that also end a NON-sensitive value (tab LF CR + : = @ [ ] |)
// so a benign pair cannot swallow the sensitive pair after it. A sensitive value
// runs through them: a secret may legitimately contain `+`, `=`, `@`, ... and
// stopping there would leak its tail.
const ENCODED_SOFT_STOPS = new Set([0x09, 0x0a, 0x0d, 0x2b, 0x3a, 0x3d, 0x40, 0x5b, 0x5d, 0x7c])
const LITERAL_VALUE_STOP_RE = /[&+\s"'#<>;,}]/
const LITERAL_KEY_CHAR_RE = /[A-Za-z0-9_% -]/
const LETTER_RE = /[A-Za-z]/
const WORD_CHAR_RE = /\w/
const AUTH_SCHEME_RE = /^(?:bearer|basic)$/i

function hexValue(code) {
  if (code >= 48 && code <= 57) return code - 48
  if (code >= 65 && code <= 70) return code - 55
  if (code >= 97 && code <= 102) return code - 87
  return -1
}

// Length of the escape starting at s[i], or 0 when s[i] does not start one.
function escapeLength(s, i) {
  if (s.charCodeAt(i) !== 37) return 0
  let depth = 0
  while (depth < MAX_ENCODING_DEPTH && s.startsWith('25', i + 1 + 2 * depth)) depth++
  for (; depth >= 0; depth--) {
    const h = i + 1 + 2 * depth
    if (hexValue(s.charCodeAt(h)) >= 0 && hexValue(s.charCodeAt(h + 1)) >= 0) return 3 + 2 * depth
  }
  return 0
}

function escapedByte(s, i, len) {
  return hexValue(s.charCodeAt(i + len - 2)) * 16 + hexValue(s.charCodeAt(i + len - 1))
}

function isEncodedQuote(s, i, len) {
  if (!len) return false
  const b = escapedByte(s, i, len)
  return b === 0x22 || b === 0x27
}

// Length of an encoded `=` / `:` separator at s[i], optionally wrapped in encoded
// quotes (`%22%3A%22`) and followed by spaces, or 0 when there is none.
function encodedSeparatorLength(s, i) {
  let j = i
  let len = escapeLength(s, j)
  if (isEncodedQuote(s, j, len)) {
    j += len
    len = escapeLength(s, j)
  }
  if (!len) return 0
  const b = escapedByte(s, j, len)
  if (b !== 0x3d && b !== 0x3a) return 0
  j += len
  // Spaces after the separator (`%22password%22%3A%20%22…`), as the literal
  // `key: value` pass allows.
  for (;;) {
    len = escapeLength(s, j)
    if (len && escapedByte(s, j, len) === 0x20) j += len
    else if (s[j] === ' ' || s[j] === '+') j++
    else break
  }
  if (isEncodedQuote(s, j, len)) j += len
  return j - i
}

function encodedValueEnd(s, i, sensitive) {
  while (i < s.length) {
    const len = escapeLength(s, i)
    if (len) {
      const b = escapedByte(s, i, len)
      if (ENCODED_HARD_STOPS.has(b) || (!sensitive && ENCODED_SOFT_STOPS.has(b))) break
      i += len
    } else if (LITERAL_VALUE_STOP_RE.test(s[i])) {
      break
    } else {
      i++
    }
  }
  return i
}

function decodeEscapes(s) {
  let out = ''
  for (let i = 0; i < s.length;) {
    const len = escapeLength(s, i)
    if (len) {
      out += String.fromCharCode(escapedByte(s, i, len))
      i += len
    } else {
      out += s[i++]
    }
  }
  return out
}

// A key starts with a letter at a word boundary or right after an escape, and runs
// over letters, digits, `_`, `-`, space and non-delimiter escapes up to an
// encoded `=` / `:`. The value then runs to the next stop (see above).
function redactEncodedPairs(s) {
  if (!s.includes('%')) return s
  let out = ''
  let copied = 0
  let keyStart = -1
  let afterEscape = false
  for (let i = 0; i < s.length;) {
    if (keyStart !== -1) {
      const sepLen = encodedSeparatorLength(s, i)
      if (sepLen) {
        const valueStart = i + sepLen
        const sensitive = SENSITIVE_KEY_RE.test(decodeEscapes(s.slice(keyStart, i)))
        let valueEnd = encodedValueEnd(s, valueStart, sensitive)
        // `Authorization%3ABearer%20<token>`: the credential follows the scheme word.
        if (sensitive && AUTH_SCHEME_RE.test(s.slice(valueStart, valueEnd))) {
          const spaceLen = escapeLength(s, valueEnd)
          const next = valueEnd + (spaceLen && escapedByte(s, valueEnd, spaceLen) === 0x20 ? spaceLen : s[valueEnd] === '+' || s[valueEnd] === ' ' ? 1 : 0)
          if (next > valueEnd) valueEnd = encodedValueEnd(s, next, true)
        }
        if (sensitive && valueEnd > valueStart) {
          out += s.slice(copied, valueStart) + REDACTED
          copied = valueEnd
        }
        keyStart = -1
        afterEscape = true
        i = valueEnd
        continue
      }
    }
    const len = escapeLength(s, i)
    if (len) {
      if (ENCODED_HARD_STOPS.has(escapedByte(s, i, len))) keyStart = -1
      afterEscape = true
      i += len
      continue
    }
    const ch = s[i]
    if (!LITERAL_KEY_CHAR_RE.test(ch)) {
      keyStart = -1
    } else if (keyStart === -1 && LETTER_RE.test(ch) && (afterEscape || i === 0 || !WORD_CHAR_RE.test(s[i - 1]))) {
      keyStart = i
    }
    afterEscape = false
    i++
  }
  return copied ? out + s.slice(copied) : s
}

// JSON Web Tokens: `eyJ…` at a word boundary followed by 4+ chars, then two more
// dot-separated segments of 4+ chars. Each dotted run is matched from its start
// (the lookbehind stops restarts inside a segment) and split by hand, so the
// scan is linear even for `eyJ-eyJ-…` runs that made the old single regex
// quadratic.
const DOTTED_RUN_RE = /(?<![\w-])[\w-]+(?:\.[\w-]+){2,}/g

// `escapeEnd` is where an escape that began just before the run ends inside `seg`
// (`%3DeyJ…`), or -1.
function jwtStart(seg, escapeEnd) {
  let p = seg.indexOf('eyJ')
  while (p > 0 && seg[p - 1] !== '-' && p !== escapeEnd) p = seg.indexOf('eyJ', p + 1)
  // A later `eyJ` in the same segment would leave even fewer chars after it.
  return p !== -1 && seg.length - p >= 7 ? p : -1
}

function redactJwts(text) {
  return text.replace(DOTTED_RUN_RE, (run, offset) => {
    if (!run.includes('eyJ')) return run
    const escapeEnd = offset > 0 ? escapeLength(text, offset - 1) - 1 : -1
    const segs = run.split('.')
    const out = []
    for (let i = 0; i < segs.length;) {
      const p = i + 2 < segs.length && segs[i + 1].length >= 4 && segs[i + 2].length >= 4 ? jwtStart(segs[i], i === 0 ? escapeEnd : -1) : -1
      if (p === -1) {
        out.push(segs[i])
        i++
      } else {
        out.push(segs[i].slice(0, p) + REDACTED)
        i += 3
      }
    }
    return out.join('.')
  })
}

function luhnOk(digits) {
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    let d = digits.charCodeAt(digits.length - 1 - i) - 48
    if (i % 2 === 1) {
      d *= 2
      if (d > 9) d -= 9
    }
    sum += d
  }
  return sum % 10 === 0
}

// Each run of local-part chars is scanned once, and `nextAt` only moves forward,
// so the scan is linear. Like the regex, a match starts at the run start and
// takes the LAST `at` with a valid domain after it (only `%40` can repeat inside
// one run, since `%` is itself a local-part char).
function redactEmails(text, at) {
  let nextAt = text.indexOf(at)
  if (nextAt === -1) return text
  let out = ''
  let copied = 0
  for (let i = 0; i < text.length && nextAt !== -1;) {
    if (!EMAIL_LOCAL_CHAR_RE.test(text[i])) {
      i++
      continue
    }
    let runEnd = i + 1
    while (runEnd < text.length && EMAIL_LOCAL_CHAR_RE.test(text[runEnd])) runEnd++
    if (nextAt <= i) nextAt = text.indexOf(at, i + 1)
    const ats = []
    while (nextAt !== -1 && (nextAt === runEnd || nextAt + at.length <= runEnd)) {
      ats.push(nextAt)
      nextAt = text.indexOf(at, nextAt + 1)
    }
    let matchEnd = -1
    for (let c = ats.length - 1; c >= 0 && matchEnd === -1; c--) {
      EMAIL_DOMAIN_RE.lastIndex = ats[c] + at.length
      if (EMAIL_DOMAIN_RE.test(text)) matchEnd = EMAIL_DOMAIN_RE.lastIndex
    }
    if (matchEnd === -1) {
      i = runEnd
    } else {
      out += text.slice(copied, i) + REDACTED
      copied = matchEnd
      i = matchEnd
    }
  }
  return copied ? out + text.slice(copied) : text
}

// Upper bound on the text redactRawText scans (OPS-1494); the rest is dropped and
// replaced by a `…[truncated N chars]` marker. Every pass is linear, the cap just
// keeps a megabyte-sized body from costing anything noticeable.
export const MAX_REDACT_INPUT = 64 * 1024
const CUT_TOKEN_CHAR_RE = /[\w.~+/=%:@-]/
const CUT_DIGIT_GROUP_RE = /[\d\s().-]/

/**
 * Redact value-shaped secrets / PII from arbitrary text before it reaches a log.
 * Preserves benign diagnostic text (e.g. "502 Bad Gateway", "upstream timed
 * out") while stripping JWTs, OAuth/bearer tokens, sensitive `key: value` pairs
 * (including `phone`/`address` PII, aligned with the structured key list),
 * UNKEYED phone-number and street-address value shapes, and email addresses.
 * Input longer than MAX_REDACT_INPUT is cut there first, so the cost stays
 * bounded for any input. Non-string input is returned unchanged so callers
 * can pass it through defensively.
 * @param {string} text
 * @returns {string}
 */
export function redactRawText(text) {
  if (typeof text !== 'string' || text.length <= MAX_REDACT_INPUT) return redactRawTextUncapped(text)
  // If the cut lands inside a token-like run, drop that whole run (and a trailing
  // digit group such as a card/phone prefix) so no partial secret survives that
  // the shape passes can no longer recognize.
  let keep = MAX_REDACT_INPUT
  if (CUT_TOKEN_CHAR_RE.test(text[keep])) {
    while (keep > 0 && CUT_TOKEN_CHAR_RE.test(text[keep - 1])) keep--
    while (keep > 0 && CUT_DIGIT_GROUP_RE.test(text[keep - 1])) keep--
  }
  return `${redactRawTextUncapped(text.slice(0, keep))}…[truncated ${text.length - keep} chars]`
}

/**
 * redactRawText without the input cap. Exported for the perf tests only; callers
 * must use redactRawText.
 * @param {string} text
 * @returns {string}
 */
export function redactRawTextUncapped(text) {
  if (typeof text !== 'string') return text
  // Shape passes run first: the keyed passes below take only the first chunk of
  // a value, which would break a JWT, a spaced card number or a street address
  // apart and leave its tail unrecognizable (OPS-1494). A keyed `phone=…` /
  // `address: …` / `token%3D…` still ends up as `key=[redacted]`.
  let out = redactJwts(text)
  for (const re of RAW_TOKEN_RES) out = out.replace(re, REDACTED)
  out = out.replace(RAW_CARD_GROUPED_RE, REDACTED)
  out = out.replace(RAW_CARD_BARE_RE, (m) => (luhnOk(m) ? REDACTED : m))
  out = out.replace(RAW_ADDRESS_VALUE_RE, REDACTED)
  out = out.replace(RAW_PHONE_VALUE_RE, REDACTED)
  out = out.replace(RAW_BEARER_RE, `bearer ${REDACTED}`)
  // Match encoded separators without decoding the diagnostic: benign text stays
  // byte-identical, and a benign pair cannot swallow a later sensitive pair.
  out = redactEncodedPairs(out)
  out = out.replace(RAW_KEYVALUE_RE, (_m, key, sep) => `${key}${sep}${REDACTED}`)
  out = out.replace(RAW_ADDRESS_RE, (_m, key, sep) => `${key}${sep}${REDACTED}`)
  out = redactEmails(out, '@')
  return out
}

// Query/fragment parameter names whose VALUE is a live single-use or bearer
// credential that must never reach an observability sink (Better Stack log `url`,
// a Sentry `request.url` / navigation breadcrumb, or Session Replay). The
// canonical offender is `token_hash` on `/auth/confirm` (a live verifyOtp /
// password-reset token, OPS-803), but the same treatment applies to any OAuth /
// magic-link token that could land in a URL now or later. Matched
// case-insensitively against the decoded param key.
// This is the URL-param denylist. It deliberately DIVERGES from
// `RAW_KEYVALUE_RE`'s key list above: `code` / `otp` are safe (and important) to
// scrub as a structured URL param but too generic to scrub in free text
// (`code=200`, `otp` prose), so they live only here; conversely `secret` /
// `password` / `cookie` / phone remain free-text concerns. Email is also
// scrubbed here so encoded addresses cannot survive in URL query values.
const URL_SECRET_PARAMS = new Set([
  'token_hash',
  'access_token',
  'refresh_token',
  'code',
  'token',
  'otp',
  'id_token',
  'email',
])

// Redact the values of denylisted params inside a raw `key=value&key2=value2`
// string (a URL query WITHOUT the leading `?`, or a param-shaped fragment WITHOUT
// the leading `#`). Only the value of a matched key is replaced — every other
// pair is preserved BYTE-FOR-BYTE (no re-encoding), so a normal URL round-trips
// unchanged and a redacted one still reads naturally (`token_hash=[redacted]`).
function redactParamString(params) {
  if (!params) return params
  return params
    .split('&')
    .map((pair) => {
      if (!pair) return pair
      const eq = pair.indexOf('=')
      const rawKey = eq === -1 ? pair : pair.slice(0, eq)
      // Param keys are effectively never percent-encoded; only pay for the decode
      // (and its try/catch) on the rare key that actually contains a `%`.
      let key = rawKey
      if (rawKey.includes('%')) {
        try {
          key = decodeURIComponent(rawKey)
        } catch {
          // Malformed percent-encoding — fall back to the raw key.
        }
      }
      return URL_SECRET_PARAMS.has(key.toLowerCase()) ? `${rawKey}=${REDACTED}` : pair
    })
    .join('&')
}

/**
 * Redact live-credential values out of a URL's query AND fragment before the URL
 * reaches an observability sink, returning a URL that is safe to log/replay but
 * otherwise identical.
 *
 * Deliberately operates by string surgery (split off `#fragment`, then `?query`)
 * rather than `new URL()`: navigation breadcrumbs (`data.to` / `data.from`) and
 * some `request.url` values are RELATIVE (`/auth/confirm?token_hash=…`), which
 * `new URL()` would throw on — silently passing the token through. String surgery
 * scrubs relative and absolute URLs alike. Only the values of denylisted params
 * (`URL_SECRET_PARAMS`) are touched; if nothing matches, the ORIGINAL string is
 * returned unchanged (byte-identical), so a benign URL like
 * `/dashboard?tab=crm` is never mangled. Non-string input is returned as-is so
 * callers can pass it through defensively.
 * @param {string} rawUrl
 * @returns {string}
 */
export function redactUrl(rawUrl) {
  if (typeof rawUrl !== 'string' || !rawUrl) return rawUrl

  const hashIdx = rawUrl.indexOf('#')
  const beforeHash = hashIdx === -1 ? rawUrl : rawUrl.slice(0, hashIdx)
  const fragment = hashIdx === -1 ? '' : rawUrl.slice(hashIdx + 1)

  const qIdx = beforeHash.indexOf('?')
  const path = qIdx === -1 ? beforeHash : beforeHash.slice(0, qIdx)
  const query = qIdx === -1 ? '' : beforeHash.slice(qIdx + 1)

  const newQuery = redactParamString(query)
  const newFragment = redactParamString(fragment)
  if (newQuery === query && newFragment === fragment) return redactEmails(redactEmails(rawUrl, '@'), '%40')

  let out = path
  if (qIdx !== -1) out += `?${newQuery}`
  if (hashIdx !== -1) out += `#${newFragment}`
  return redactEmails(redactEmails(out, '@'), '%40')
}

/**
 * Scrub a URL-ish string: free-text redaction (encoded `%3D` pairs, emails,
 * tokens) THEN param-denylist redaction. Use for every URL that reaches a sink.
 * @param {string} value
 * @returns {string}
 */
export function scrubUrlString(value) {
  if (typeof value !== 'string' || !value) return value
  return redactUrl(redactRawText(value))
}

/**
 * Recursively redact a JSON structure so it never leaks tokens / OAuth
 * diagnostics / customer PII when logged. Two layers, because a leak can hide
 * either in a key or in a value:
 *   - by key: any value under a sensitive key name becomes `[redacted]`.
 *   - by value: every *string* value (at any key, sensitive or not) is run
 *     through `redactRawText`, which strips value-shaped secrets/PII. This closes
 *     the gap where a backend echoes a secret inside a non-sensitive key — e.g.
 *     `{ error: "Bearer ya29.… for a@b.com failed" }` — which key-name redaction
 *     alone would pass through untouched.
 * Returns a new structure; the input (from JSON.parse) is left untouched. A depth
 * guard keeps pathologically nested payloads cheap.
 * @param {unknown} value
 * @param {number} [depth]
 * @returns {unknown}
 */
export function redactSensitive(value, depth = 0) {
  if (typeof value === 'string') return redactRawText(value)
  if (value === null || typeof value !== 'object') return value
  if (depth >= MAX_REDACT_DEPTH) return '[truncated-depth]'
  if (Array.isArray(value)) return value.map((v) => redactSensitive(v, depth + 1))
  const out = {}
  for (const [key, val] of Object.entries(value)) {
    out[key] = SENSITIVE_KEY_RE.test(key) ? REDACTED : redactSensitive(val, depth + 1)
  }
  return out
}

// Cap on the serialized size of a `responseBody` attached to a logged error, so
// an unbounded upstream payload — a multi-KB 502 HTML page, a large JSON proxy
// dump — is never shipped to BetterStack in full. The cap lives HERE, beside the
// redaction primitives, rather than in the QuickList HTTP client, so every log
// boundary that records a response body enforces the same bound from one place.
export const MAX_LOGGED_BODY = 2000

/**
 * Redact AND size-bound a `responseBody` of unknown shape before it is logged.
 * This is the single shared body-safety boundary used by BOTH `serializeError()`
 * (in `betterstack-logger.js`) and the QuickList HTTP client, so bounding is
 * enforced at the reusable boundary instead of being duplicated per caller
 * (OPS-56, Codex review: the shared `serializeError` contract must bound any
 * attached body, not only the QuickList path).
 *   - string → value-shape redaction, then truncation to `max`. Redact BEFORE
 *     truncating so a token straddling the cap can't survive as a partial value.
 *   - object/array → recursive key+value redaction; if the serialized form still
 *     exceeds `max` it collapses to a truncated string, otherwise the redacted
 *     structure is returned.
 *   - anything else → returned unchanged.
 * Idempotent for already-redacted/bounded input: the QuickList client bounds on
 * its own path and `serializeError` re-applies this at the log boundary.
 * @param {unknown} body
 * @param {number} [max]
 * @returns {unknown}
 */
export function boundRedactedBody(body, max = MAX_LOGGED_BODY) {
  if (typeof body === 'string') {
    const safe = redactRawText(body)
    return safe.length > max ? `${safe.slice(0, max)}…[truncated]` : safe
  }
  if (body !== null && typeof body === 'object') {
    const redacted = redactSensitive(body)
    let serialized = ''
    try {
      serialized = JSON.stringify(redacted)
    } catch {
      serialized = ''
    }
    if (serialized.length > max) {
      return `${serialized.slice(0, max)}…[truncated]`
    }
    return redacted
  }
  return body
}
